import { useState } from 'react';
import {
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuOptionGroup,
  MenuItemOption,
  MenuDivider,
  Button,
  Text,
  Box,
  Icon,
  IconButton,
  Tooltip,
  Flex
} from '@chakra-ui/react';
import { ChevronDownIcon } from '@chakra-ui/icons';
import { FaEdit, FaTrash } from 'react-icons/fa';
import { AiOutlinePlusCircle } from 'react-icons/ai'
import { AddressModel } from 'models';
import { getCountryNameFromCountryCode } from 'utils/country';

export interface AddressDropdownProps {
  addresses: AddressModel[];
  defaultAddress?: AddressModel;
  onSelect?: (address: AddressModel) => void;
  size?: 'sm' | 'md' | 'lg';
  addNewLabel?: string;
  mode?: 'select' | 'edit';
  onAddAddress?: () => void;
  onEditAddress?: (address: AddressModel) => void;
  onRemoveAddress?: (address: AddressModel) => void;
}

export default function AddressDropdown({
  addresses,
  defaultAddress,
  onSelect = () => { },
  size = 'sm',
  addNewLabel = 'Add New Address',
  mode = 'select',
  onAddAddress,
  onEditAddress,
  onRemoveAddress,
}: AddressDropdownProps) {
  const defaultIdx = defaultAddress
    ? addresses.findIndex((a) => a.isEqual(defaultAddress))
    : 0;
  const [selectedIdx, setSelectedIdx] = useState<number>(defaultIdx);
  const handleSelect = (idx: number) => {
    setSelectedIdx(idx);
    onSelect(addresses[idx]);
  };

  const displayText =
    addresses[selectedIdx]?.getDisplayText() ??
    (mode === 'edit' ? 'Manage Addresses' : 'Select Address');
  const showAddNew = Boolean(onAddAddress);

  function renderAddressLabel(addr: AddressModel, bold: boolean) {
    return (
      <Box textAlign="left" fontWeight={bold ? 'bold' : 'normal'}>
        <Text fontSize="sm">
          {addr.line1}{addr.line2 && `, ${addr.line2}`}
        </Text>
        <Text fontSize="sm">
          {addr.city}, {addr.state}, {addr.postalCode}
        </Text>
        {addr.country && (
          <Text fontSize="sm">
            {getCountryNameFromCountryCode(addr.country)}
          </Text>
        )}
      </Box>
    );
  }

  return (
    <Menu closeOnSelect={mode === 'edit'}>
      <MenuButton
        as={Button}
        rightIcon={<ChevronDownIcon />}
        size={size}
        variant="outline"
        w="full"
        textAlign="left"
        maxW={{ base: 200, md: 300 }}
      >
        <Text fontSize={size} isTruncated>
          {displayText}
        </Text>
      </MenuButton>

      <MenuList minW={0} maxH="300px" overflowY="auto" zIndex={200}>
        {mode === 'select' ? (
          <MenuOptionGroup
            title="Select address"
            type="radio"
            defaultValue={String(selectedIdx)}
            onChange={(val) => handleSelect(Number(val))}
          >
            {addresses.map((addr, idx) => (
              <MenuItemOption key={idx} value={String(idx)} px={4} py={2}>
                {renderAddressLabel(addr, idx === 0)}
              </MenuItemOption>
            ))}
          </MenuOptionGroup>
        ) : (
          addresses.map((addr, idx) => (
            <MenuItem
              key={idx}
              px={4}
              py={2}
              justifyContent="space-between"
              _hover={{ bg: 'transparent' }}
            >
              {renderAddressLabel(addr, idx === 0)}
              <Tooltip label={idx === 0 ? 'Edit Address' : 'Remove Address'}>
                <IconButton
                  aria-label={idx === 0 ? 'Edit primary address' : 'Remove secondary address'}
                  variant="ghost"
                  size="sm"
                  icon={
                    <Icon
                      as={idx === 0 ? FaEdit : FaTrash}
                      boxSize={4}
                      _hover={{
                        color: idx === 0 ? 'blue.500' : 'red.500',
                        cursor: 'pointer',
                      }}
                    />
                  }
                  onClick={() =>
                    idx === 0
                      ? onEditAddress?.(addr)
                      : onRemoveAddress?.(addr)
                  }
                />
              </Tooltip>
            </MenuItem>
          ))
        )}
        {showAddNew && (
          <>
            <MenuDivider />
            <MenuItem
              onClick={onAddAddress}
              color="blue.500"
              justifyContent="center"
              textAlign="center"
              fontSize={size}
              px={4}
              py={2}
            >
              <Flex gap={1} align="center">
                <Icon as={AiOutlinePlusCircle} />
                <Text fontWeight={600}>{addNewLabel}</Text>
              </Flex>
            </MenuItem>
          </>
        )}
      </MenuList>
    </Menu>
  );
}
